import React from 'react';
import {
  Grid,
  GridCellProps,
  GridNoRecords,
  GridColumn as Column,
  GridColumnMenuFilter,
  GridDataStateChangeEvent
} from '@progress/kendo-react-grid';
import {
  ExcelExport,
  ExcelExportColumn
} from '@progress/kendo-react-excel-export';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import { State, toDataSourceRequestString } from '@progress/kendo-data-query';

import {
  GridToolBar,
  GridToolbarDataStateChangeEvent
} from '../components/grid/grid-tool-bar';
import Toolbar from '../components/page-toolbar';
import { GetChallenges } from '../services/events';
import LinkCell from '../components/grid/link-cell';
import { formatDateToDDMMYYYY } from '../utils/date';
import { NoRecords } from '../components/grid/no-records';
import { DefaultGridSettings } from '../constants/grid-settings';
import { getStatusBadge } from '../components/activity-event/wst';
import { useGetSubmissionsApprovalQueue } from '../hooks/services/use-challenges';
import { Loader } from '@progress/kendo-react-indicators';

const initialDataState: State = {
  sort: [{ field: 'submittedDateUtc', dir: 'desc' }],
  ...DefaultGridSettings.initialDataState
};

export const StatusFilterUI = (props: any) => {
  const { firstFilterProps } = props;
  const rawValue = firstFilterProps.value ?? '';
  let value = rawValue;
  if (rawValue === 'PENDING_REVIEW') {
    value = 'Reviewing';
  } else if (rawValue.toUpperCase() === 'REJECTED') {
    value = 'Declined';
  }

  const handleChange = (event: any) => {
    let targetVal = event.target.value;
    if (targetVal === 'All') {
      targetVal = '';
    } else if (targetVal === 'Reviewing') {
      targetVal = 'PENDING_REVIEW';
    } else if (targetVal === 'Declined') {
      targetVal = 'Rejected';
    }

    firstFilterProps.onChange({
      value: targetVal,
      operator: 'eq',
      syntheticEvent: event.syntheticEvent
    });
  };

  return (
    <DropDownList
      data={['All', 'Reviewing', 'Approved', 'Declined']}
      value={value || 'All'}
      onChange={handleChange}
    />
  );
};

export const ChallengeFilterUI = (props: any) => {
  const { firstFilterProps } = props;
  const value = firstFilterProps.value ?? '';
  const [challenges, setChallenges] = React.useState<string[]>([]);

  React.useEffect(() => {
    GetChallenges().then((res) => {
      setChallenges(['All', ...res.map((c) => c.name || '')]);
    });
  }, []);

  const handleChange = (event: any) => {
    firstFilterProps.onChange({
      value: event.target.value === 'All' ? '' : event.target.value,
      operator: 'eq',
      syntheticEvent: event.syntheticEvent
    });
  };

  return (
    <DropDownList
      data={challenges}
      value={value || 'All'}
      onChange={handleChange}
    />
  );
};

export default function WstApprovalGrid() {
  const [dataState, setDataState] = React.useState<State>(initialDataState);
  const gridExportRef = React.useRef(null);

  const { data, isLoading, mutate } = useGetSubmissionsApprovalQueue(
    toDataSourceRequestString(dataState)
  );

  const DateCell = (props: GridCellProps, fieldName: string) => {
    const val = props.dataItem[fieldName];
    return (
      <td className={'k-table-td'}>{val ? formatDateToDDMMYYYY(val) : ''}</td>
    );
  };

  const StatusName = (props: GridCellProps) => {
    const statusInfo = getStatusBadge(props.dataItem.status);

    return (
      <td className={'k-table-td text-center'}>
        <span
          style={{
            padding: '4px 8px',
            borderRadius: '5px',
            fontSize: '14px',
            ...statusInfo.style
          }}>
          {statusInfo.text}
        </span>
      </td>
    );
  };

  const onActionRow = (props: GridCellProps) => {
    return (
      <LinkCell
        {...props}
        to={`${props.dataItem.eventId}/${props.dataItem.eventPlaylistId}/${props.dataItem.id}`}
        valueCell={'View Submission'}
        className={'text-center'}
      />
    );
  };

  React.useEffect(() => {
    mutate();
  }, [dataState]);

  return (
    <>
      <Toolbar title={'WST Challenge Pending Approval'} />
      <div
        className={'pt-2'}
        style={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          width: '100%',
          overflow: 'hidden'
        }}>
        <GridToolBar
          searchPlaceholder={'Search challenge submissions'}
          columnsToSearch={[
            'challengeName',
            'submittedByName',
            'eventName',
            'organisationName'
          ]}
          exportRef={gridExportRef}
          showGridMode={false}
          showCardMode={false}
          {...dataState}
          onDataStateChange={(e: GridToolbarDataStateChangeEvent) => {
            setDataState(e.dataState);
          }}
        />

        <ExcelExport
          data={(data?.data ?? []).map((data) => ({
            ...data,
            status: getStatusBadge(data.status).text
          }))}
          ref={gridExportRef}
          fileName={'wst-challenge-pending-approval.xlsx'}>
          <ExcelExportColumn
            field={'eventDateUtc'}
            title={'Event date'}
            width={200}
          />
          <ExcelExportColumn field={'eventName'} title={'Event title'} />
          <ExcelExportColumn field={'challengeName'} title={'Challenge'} />
          <ExcelExportColumn
            field={'submittedDateUtc'}
            title={'Submitted Date'}
            width={200}
          />
          <ExcelExportColumn field={'submittedByName'} title={'Submitted by'} />
          <ExcelExportColumn field={'status'} title={'Status'} />
        </ExcelExport>

        <Grid
          id={'grid-wst-approval'}
          style={{
            flexGrow: 1,
            minHeight: 0,
            height: '100%',
            overflow: 'hidden'
          }}
          pageable={DefaultGridSettings.pagerSettings}
          sortable={true}
          data={data}
          {...dataState}
          onDataStateChange={(e: GridDataStateChangeEvent) => {
            setDataState(e.dataState);
          }}>
          <Column
            field={'eventDateUtc'}
            title={'Event date'}
            sortable={true}
            cells={{
              data: (props) => DateCell(props, 'eventDateUtc')
            }}
          />
          <Column field="eventName" title="Event title" sortable />
          <Column
            field={'challengeName'}
            title={'Challenge'}
            sortable
            columnMenu={(props: any) => (
              <GridColumnMenuFilter {...props} filterUI={ChallengeFilterUI} />
            )}
          />
          <Column
            field={'submittedDateUtc'}
            title={'Submitted Date'}
            sortable
            cells={{
              data: (props) => DateCell(props, 'submittedDateUtc')
            }}
          />
          <Column field={'submittedByName'} title={'Submitted by'} sortable />
          <Column
            field={'status'}
            title={'Status'}
            sortable
            cells={{
              data: (props) => StatusName(props)
            }}
            columnMenu={(props: any) => (
              <GridColumnMenuFilter {...props} filterUI={StatusFilterUI} />
            )}
          />
          <Column
            title={'Actions'}
            cells={{
              data: (props) => onActionRow(props)
            }}
          />

          <GridNoRecords>
            {isLoading ? (
              <div
                className={
                  'd-flex flex-column align-items-center gap-2 text-muted'
                }>
                <Loader type={'pulsing'} />
                <span>Loading data</span>
              </div>
            ) : (
              <NoRecords />
            )}
          </GridNoRecords>
        </Grid>
      </div>
    </>
  );
}
